import type React from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import type { SelectOptions } from "components/SelectUI/Select.model";

import { DEFAULT_ROWS_PER_PAGE, ROWS_PER_PAGE } from "constants/misc-constants";
import { StyledField, StyledInput, StyledSelect, StyledUsersFilter } from "./UsersFilter.styled";

interface UsersFilterProps {
  educations: SelectOptions[];
  schools: SelectOptions[];
  selectedEducation?: string;
  selectedSchool?: string;
  searchValueParam: string;
  selectedUserStatus: string;
  onEducationChange: (education: string) => void;
  onSearchValueChange: (searchValueParam: string) => void;
  onSchoolChange: (school: string) => void;
  selectedResults: number;
  onResultsChange: (results: number) => void;
  onStatusChange: (status: string) => void;
}

export const UsersFilter: React.FC<UsersFilterProps> = ({
  searchValueParam,
  schools,
  educations,
  onSearchValueChange,
  onEducationChange,
  onSchoolChange,
  selectedEducation,
  selectedSchool,
  onResultsChange,
  onStatusChange,
  selectedUserStatus,
  selectedResults,
}) => {
  const { t } = useTranslation("admin-environment");
  const [searchValue, setSearchValue] = useState(searchValueParam);

  const resultsPerPage = ROWS_PER_PAGE.map((rows) => ({
    value: rows,
    label: `${rows}`,
  }));

  useEffect(() => {
    if (!searchValue && !!searchValueParam) {
      setSearchValue(searchValueParam);
    }
  }, [searchValueParam]);

  return (
    <StyledUsersFilter>
      <StyledField>
        <label htmlFor="input">
          {t("userFilter.input.label", "Search on lastname, email or student number")}
        </label>
        <StyledInput
          data-cy="accounts-filter-input"
          id="input"
          value={searchValue}
          onBlur={() => onSearchValueChange(searchValue)}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchValue(e.target.value)}
        />
      </StyledField>
      <StyledSelect
        showSearchBox
        aria-label="school-selector"
        dataCy="school-selector"
        defaultValue="all"
        options={schools}
        value={selectedSchool}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => onSchoolChange(e.target.value)}
      />
      <StyledSelect
        showSearchBox
        aria-label="education-selector"
        dataCy="education-selector"
        defaultValue="all"
        disabled={!selectedSchool || selectedSchool === "all"}
        options={educations}
        value={selectedEducation}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => onEducationChange(e.target.value)}
      />
      <StyledSelect
        aria-label="status-selector"
        dataCy="status-selector"
        defaultValue="all"
        options={[
          { value: "all", label: t("userFilter.status.all", "All") },
          { value: "active", label: t("userFilter.status.active", "Active") },
          { value: "inactive", label: t("userFilter.status.inactive", "Inactive") },
        ]}
        value={selectedUserStatus}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
          onStatusChange(e.target.value);
        }}
      />
      <StyledSelect
        aria-label="results-selector"
        dataCy="results-selector"
        defaultValue={DEFAULT_ROWS_PER_PAGE}
        options={resultsPerPage}
        value={selectedResults}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
          onResultsChange(Number.parseInt(e.target.value, 10))
        }
      />
    </StyledUsersFilter>
  );
};
